// Error-code → localization-key map for the Receiving & Racks flow.
// Contract (darkstore-api-contract.md §2): "Apps show `message` when
// present, else a local string for the code below." The axios interceptor
// (src/services/api-utils.js) already auto-toasts `response.data.message`
// on any 400, so callers here should only fall back to this map when that
// message was empty — see shouldShowLocalError().
//
// Known gap (pre-existing, app-wide — not touched here): that same
// interceptor's onRejectedResponseInterceptor only *rejects* the promise
// for status 400, 401, 412, 418 and 429; every other 4xx (402-499 minus
// those) hits its generic "try again" branch and returns `undefined`
// instead of `Promise.reject`, so the awaited call *resolves* with an
// empty response rather than throwing. Of this contract's codes, that
// affects only the two 404s — RACK_NOT_FOUND and LABEL_NOT_FOUND — whose
// specific Hindi/English string (from this map) can't be shown; callers
// guard for an empty response at those two call sites and fall back to
// the generic message instead (see ReceivingScanRack.handleResolve and
// ReceivingScanShipments.submitScan). Fixing it properly means either the
// backend emitting those two as 400 like every other code here, or a
// change to the shared interceptor — both out of scope for this feature.

import {
  extractErrorCode,
  shouldShowLocalError,
} from "@components/ds/apiErrors";

export { extractErrorCode, shouldShowLocalError };

const CODE_TO_KEY = {
  // POST /racks/resolve
  RACK_NOT_FOUND: "rackNotFound",
  RACK_OTHER_CMO: "rackOtherCmo",
  RACK_DISABLED: "rackDisabled",
  // /racks/resolve (with cx_id) and /tps/:cx_id/scan — 1 rack : 1 customer
  RACK_OTHER_CX: "rackOtherCx",
  // same customer, another delivery date (an advance-racked card)
  RACK_OTHER_EDD: "rackOtherEdd",
  // POST /tps/:cx_id/scan
  LABEL_NOT_FOUND: "labelNotFound",
  LABEL_OLD: "labelOld",
  LABEL_NOT_PACKED: "labelNotPacked",
  LABEL_OTHER_TP: "labelOtherTp",
  LABEL_OTHER_DATE: "labelOtherDate",
  LABEL_OTHER_CP: "labelOtherCp",
  RACK_FULL: "rackFull",
  // POST /tps/:cx_id/complete
  NOT_ALL_RACKED: "notAllRacked",
  LABELS_MISSING: "labelsMissing",
};

const GENERIC_KEY = "generic";

/**
 * @param {string|undefined|null} code One of the CODE strings the contract
 *   lists, e.g. "LABEL_OLD".
 * @returns {string} A `Receiving/errors/<key>` path for the `t()` helper.
 */
export function getErrorMessageKey(code) {
  const key = code && CODE_TO_KEY[code] ? CODE_TO_KEY[code] : GENERIC_KEY;
  return `Receiving/errors/${key}`;
}

/** `data.pending` on a 400 NOT_ALL_RACKED — the still-unracked shipment ids. */
export function extractPendingShipments(error) {
  const data = error?.response?.data || {};
  return Array.isArray(data.pending) ? data.pending : [];
}
